import LockIcon from '@mui/icons-material/Lock';
import { Box, Button, ButtonProps, IconButton, styled, Typography } from '@mui/material';
import { Side } from '@protos/trading';
import { PermissionsEnum } from '@protos/user';
import { OtcExchange, OtcOrderType } from '@protos/v2/otcQuote';
import { priceFormatterService } from '@services/PriceFormatterService';
import { useUserSettingsContext } from '@shared/contexts/UserContextProvider';
import { countDecimals } from '@utils/number';
import { useEffect, useMemo, useState } from 'react';
import { useTradingContext } from '../TradingContext';

export interface TradingButtonsProps {
  rfqSubscriptionSymbol: string;
  rfqSubscriptionSize: number;
  rfSubscriptionExchange: OtcExchange;
  isDisabled: boolean;
  tradingAccount: string;
  tradeSize: number;
  isLockedBuy?: boolean;
  isLockedSell?: boolean;
  lockedBuyPrice?: string;
  lockedSellPrice?: string;
  onToggleBuyLock: (price?: string) => void;
  onToggleSellLock: (price?: string) => void;
  isBelowMinTrade: boolean;
  isQuantityUnitSpecified: boolean;
  showTradeConfirmation: (otcSide: Side) => void;
  onPlaceTrade: (key: Side) => void;
  orderType: OtcOrderType;
  limitTradeState: Side;
  limitBuyPrice: number;
  limitSellPrice: number;
  isMocAfterSettlementTime: boolean;
}

type TradingButtonProps = { isBuy?: boolean } & ButtonProps;
const TradingButton = styled(Button, { shouldForwardProp: propName => propName !== 'isBuy' })<TradingButtonProps>(({ theme, isBuy }) => ({
  borderRadius: 8,
  width: '100%',
  backgroundColor: isBuy ? theme.palette.background.buy : theme.palette.background.sell,
  display: 'flex',
  flexDirection: 'column',
  padding: 5,
  height: 48,
  marginBottom: 5,
  transition: 'all 0.2s ease',
  border: '2px solid rgba(0,0,0,0)',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: isBuy ? theme.palette.background.buyHover + ' !important' : theme.palette.background.sellHover + ' !important',

    border: '2px solid rgba(0, 0, 0, 10)',
    borderRadius: 8,
  },

  '&:focus': {
    backgroundColor: isBuy ? theme.palette.background.buy + ' !important' : theme.palette.background.sell + ' !important',
    outline: 'none',
  },

  '&:focus-visible': {
    backgroundColor: isBuy ? theme.palette.background.buy + ' !important' : theme.palette.background.sell + ' !important',
    outline: `2px solid ${isBuy ? '#6096eb' : '#c96565'}`,
    outlineOffset: '2px',
  },
}));

export const TradingButtons = ({
  rfqSubscriptionSymbol,
  rfqSubscriptionSize,
  isDisabled,
  tradeSize,
  isLockedBuy,
  isLockedSell,
  lockedBuyPrice,
  lockedSellPrice,
  onToggleBuyLock,
  onToggleSellLock,
  isBelowMinTrade,
  isQuantityUnitSpecified,
  showTradeConfirmation,
  onPlaceTrade,
  orderType,
  limitTradeState,
  isMocAfterSettlementTime,
}: TradingButtonsProps) => {
  const { prices, isLoadingPrices } = useTradingContext();

  const settingsContext = useUserSettingsContext();
  if (!settingsContext) {
    return <div>No user found...</div>;
  }

  const { user, userSettings } = settingsContext;

  // Determine whether to show the trade confirmation popup

  // Internal users default to 'no confirmation' (false); external users default to 'show confirmation' (true)
  const defaultShowConfirmationSetting = user.permissions.includes(PermissionsEnum.ONYX_USER) ? false : true;

  // The user may or may not have overridden this setting, but it won't be there unless they have
  const userShowConfirmationSetting = userSettings?.widgets?.trading_v2?.showConfirmationOnTrade;

  // So, if they have set it, use that setting; if it's missing, use the default
  const shouldShowTradeConfirmationDialog = userShowConfirmationSetting === undefined ? defaultShowConfirmationSetting : userShowConfirmationSetting;

  // Add validation for trade size
  const isValidTradeSize = rfqSubscriptionSize > 0 && Number.isInteger(rfqSubscriptionSize);

  // Update button disabled state to include trade size validation
  const areButtonsDisabled =
    isDisabled ||
    !isValidTradeSize ||
    isLoadingPrices ||
    !isQuantityUnitSpecified ||
    (orderType === OtcOrderType.limit && limitTradeState === Side.UNSPECIFIED);

  const isLimitOrder = orderType === OtcOrderType.limit;

  const rawCanSell = useMemo(() => {
    return prices.sell && Number(prices.sell.amount) >= tradeSize;
  }, [prices, tradeSize]);

  const rawCanBuy = useMemo(() => prices.buy && Number(prices.buy.amount) >= tradeSize, [prices, tradeSize]);

  const priceDecimals = useMemo(() => {
    const buyDecimals = prices.buy ? countDecimals(prices.buy.price) : 3;
    const sellDecimals = prices.sell ? countDecimals(prices.sell.price) : 3;
    return buyDecimals > sellDecimals ? buyDecimals : sellDecimals;
  }, [prices]);

  const differenceDisplay = useMemo(() => {
    if (isMocAfterSettlementTime) return 'Market is closed';
    if (!prices.buy || !prices.sell) return 'N/A';
    const spread = Math.abs(Number(prices.buy.price) - Number(prices.sell.price));
    return spread.toFixed(priceDecimals);
  }, [isMocAfterSettlementTime, prices.buy, prices.sell, priceDecimals]);

  const [showSizeUnavailable, setShowSizeUnavailable] = useState(false);

  // Allow 500ms to see whether a price is returned rather than instantly showing the error
  useEffect(() => {
    if (!rawCanBuy && !rawCanSell) {
      const timer = setTimeout(() => {
        setShowSizeUnavailable(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowSizeUnavailable(false);
      return;
    }
  }, [rawCanBuy, rawCanSell]);

  const onButtonClicked = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, key: Side) => {
    e.preventDefault();
    if (!rfqSubscriptionSymbol) return;
    if (shouldShowTradeConfirmationDialog) {
      showTradeConfirmation(key);
    } else {
      onPlaceTrade(key);
    }
  };

  const onRightClick = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, price: string, side: Side) => {
    e.preventDefault();
    if (isLimitOrder) return;
    if (!rfqSubscriptionSymbol) return;
    if (side === Side.BUY) {
      onToggleBuyLock(price);
    } else {
      onToggleSellLock(price);
    }
  };

  const isDisabledBuy =
    areButtonsDisabled ||
    !rawCanBuy ||
    Boolean(isLockedBuy && lockedBuyPrice && Number(lockedBuyPrice) < Number(prices.buy?.price || 0)) ||
    isBelowMinTrade ||
    (isLimitOrder && limitTradeState !== Side.BUY);

  const isDisabledSell =
    areButtonsDisabled ||
    !rawCanSell ||
    Boolean(isLockedSell && lockedSellPrice && Number(lockedSellPrice) > Number(prices.sell?.price || 0)) ||
    isBelowMinTrade ||
    (isLimitOrder && limitTradeState !== Side.SELL);

  const allowLocks = orderType !== OtcOrderType.limit;

  const renderErrorState = (message: string) => (
    <Box display="flex" flexDirection="column" gap={1} width="100%" height="50px" justifyContent="center" alignItems="center">
      <Typography fontSize={10} color="error" textAlign="center" marginTop={2}>
        {message}
      </Typography>
    </Box>
  );

  const priceAndLockVisibility = orderType === OtcOrderType.limit ? 'none' : 'inherit';
  const ButtonTextStyle = {
    padding: 0,
    margin: 0,
    lineHeight: '1.5em',
    fontSize: 9,
  };
  const ButtonPriceStyle = {
    ...ButtonTextStyle,
    fontWeight: 800,
    fontSize: 12,
    lineHeight: '1.2em',
    marginBottom: '0px',
    display: priceAndLockVisibility,
  };

  const renderTradingButtons = () => (
    <Box
      display="flex"
      gap={1}
      width="100%"
      height="50px"
      justifyContent="center"
      position="relative"
      flexDirection={isLimitOrder ? 'row-reverse' : 'row'}
    >
      {/* Sell side */}
      <Box sx={{ position: 'absolute', top: -16, left: 0, display: 'flex', alignItems: 'center' }}>
        {isLockedSell && allowLocks && (
          <>
            <IconButton size="small" onClick={e => onRightClick(e, prices.sell?.price ?? '', Side.SELL)} sx={{ minWidth: 20, p: 0.5 }}>
              <LockIcon sx={{ fontSize: 12 }} />
            </IconButton>

            <Typography sx={{ fontSize: 10 }}>@ {lockedSellPrice}</Typography>
          </>
        )}
      </Box>
      <Box sx={{ position: 'relative', width: '100%' }} onContextMenu={e => onRightClick(e, prices.sell?.price ?? '', Side.SELL)}>
        <TradingButton
          key="sell"
          name="sell"
          sx={{ width: '100%' }}
          variant="contained"
          onClick={e => onButtonClicked(e, Side.SELL)}
          tabIndex={2}
          disabled={isDisabledSell}
          data-testid="place-order-button"
        >
          <Typography variant="body2" style={ButtonTextStyle} tabIndex={-1}>
            Sell
          </Typography>

          <Typography variant="body2" style={ButtonPriceStyle} tabIndex={-1}>
            {prices.sell ? priceFormatterService.formatProductPrice(prices.sell.price) : 'N/A'}
          </Typography>
          <Typography variant="body2" style={ButtonTextStyle} tabIndex={-1}>
            {prices.sell ? `${prices.sell.amount} Lots` : ''}
          </Typography>
        </TradingButton>
      </Box>
      <Box
        style={{
          fontSize: 14,
          position: 'absolute',
          color: '#585858',
          backgroundColor: '#FFFFFF',
          borderRadius: 2,
          alignSelf: 'end',
          zIndex: 1,
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          maxHeight: 13,
          marginBottom: -5,
        }}
      >
        {differenceDisplay}
      </Box>

      {/* Buy side */}

      <Box sx={{ position: 'absolute', top: -16, right: 0, display: 'flex', alignItems: 'center' }}>
        {isLockedBuy && allowLocks && (
          <>
            <Typography sx={{ fontSize: 10 }}>{lockedBuyPrice} @</Typography>

            <IconButton size="small" onClick={e => onRightClick(e, prices.buy?.price ?? '', Side.BUY)} sx={{ minWidth: 20, p: 0.5 }}>
              <LockIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </>
        )}
      </Box>
      <Box sx={{ position: 'relative', width: '100%' }} onContextMenu={e => onRightClick(e, prices.buy?.price ?? '', Side.BUY)}>
        <TradingButton
          key="buy"
          name="buy"
          sx={{ width: '100%' }}
          variant="contained"
          isBuy
          onClick={e => onButtonClicked(e, Side.BUY)}
          tabIndex={2}
          disabled={isDisabledBuy}
          data-testid="place-order-button"
        >
          {isLockedBuy && (
            <Box
              sx={{
                width: 'fit-content',
                marginRight: '3px',
                mt: '2px',
                right: 0,
                position: 'absolute',
                top: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            ></Box>
          )}
          <Typography variant="body2" style={ButtonTextStyle} tabIndex={-1}>
            Buy
          </Typography>
          <Typography variant="body2" style={ButtonPriceStyle} tabIndex={-1}>
            {prices.buy ? priceFormatterService.formatProductPrice(prices.buy.price) : 'N/A'}
          </Typography>
          <Typography variant="body2" style={ButtonTextStyle} tabIndex={-1}>
            {prices.buy ? `${prices.buy.amount} Lots` : ''}
          </Typography>
        </TradingButton>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (!rawCanBuy && !rawCanSell && showSizeUnavailable && !isMocAfterSettlementTime) {
      return renderErrorState('Size not available. Please contact support');
    }

    if (!isQuantityUnitSpecified) {
      return renderErrorState('Product metadata is missing. Please contact support');
    }

    return renderTradingButtons();
  };

  return rfqSubscriptionSymbol ? renderContent() : null;
};
